import { useEffect, useMemo, useState } from "react";
import { useAdminCategories } from "@/hooks/useAdminCategories";
import { useAdminDeals, useAdminProducts } from "@/hooks/useAdminStore";
import { clearDraft, readDraft, writeDraft } from "@/utils/adminDraft";
import {
  ARTICLE_TYPES,
  createEmptyArticle,
  type ArticleFaq,
  type ArticleProductReference,
  type ArticleRecord,
  type ArticleSection,
  useArticleCatalog,
} from "@/hooks/useArticleCatalog";


const createProductsSection = (): ArticleSection => ({
  id: `produtos-${Date.now()}`,
  type: "products",
  title: "Produtos em Destaque",
  content: "",
  products: [],
});

const createGuideSection = (): ArticleSection => ({
  id: `guia-${Date.now()}`,
  type: "guide",
  title: "Guia Principal",
  content: "",
});

const createComparisonSection = (): ArticleSection => ({
  id: `comparativo-${Date.now()}`,
  type: "comparison",
  title: "Comparativo",
  content: "",
  comparisonColumns: {
    first: "Opção 1",
    second: "Opção 2",
    third: "Opção 3",
  },
  comparisonTable: [],
});

const createEmptyFaq = (): ArticleFaq => ({
  question: "",
  answer: "",
});

const createEmptyProductReference = (): ArticleProductReference => ({
  productSlug: "",
  badge: "",
  highlight: "",
});

const ARTICLE_DRAFT_STORAGE_KEY = "afiliabr_admin_article_draft_v1";

function hasMeaningfulArticleDraft(article: Omit<ArticleRecord, "id">) {
  return Boolean(
    article.title.trim() ||
    article.excerpt.trim() ||
    article.image.trim() ||
    article.heroImage.trim() ||
    article.quickSummary.trim() ||
    article.metaDescription.trim() ||
    article.tags.length > 0 ||
    article.relatedArticleSlugs.length > 0 ||
    article.sections.some((section) =>
      Boolean(
        section.title?.trim() ||
        section.content.trim() ||
        section.products?.some((item) => item.productSlug.trim() || item.badge.trim() || item.highlight.trim()) ||
        section.comparisonTable?.some((row) => row.feature.trim() || row.first.trim() || row.second.trim() || row.third.trim())
      )
    ) ||
    article.faqs.some((faq) => faq.question.trim() || faq.answer.trim())
  );
}

function readStoredArticleDraft() {
  return readDraft<Omit<ArticleRecord, "id">>(ARTICLE_DRAFT_STORAGE_KEY);
}

export function hasStoredArticleDraft() {
  const draft = readStoredArticleDraft();
  return Boolean(draft && hasMeaningfulArticleDraft(draft));
}

export function clearStoredArticleDraft() {
  clearDraft(ARTICLE_DRAFT_STORAGE_KEY);
}

function sanitizeArticleForm(article: Omit<ArticleRecord, "id">) {
  return {
    ...article,
    sections: article.sections.filter((section) => section.type !== "intro"),
  };
}

interface Props {

  article?: ArticleRecord | null;
  onSave: (data: Omit<ArticleRecord, "id">) => void;
  onClose: () => void;
}

export default function ArticleModal({ article, onSave, onClose }: Props) {
  const storedDraft = useMemo(() => (!article ? readStoredArticleDraft() : null), [article]);
  const draftWasRestored = Boolean(!article && storedDraft && hasMeaningfulArticleDraft(storedDraft));
  const [form, setForm] = useState<Omit<ArticleRecord, "id">>(
    article ? sanitizeArticleForm({ ...article }) : storedDraft ? sanitizeArticleForm(storedDraft) : createEmptyArticle()
  );

  const [tagInput, setTagInput] = useState("");
  const [productFinderFilters, setProductFinderFilters] = useState<Record<string, { category: string; subcategory: string }>>({});
  const { categories } = useAdminCategories();
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();
  const { articles, articleCategories, slugify } = useArticleCatalog();

  useEffect(() => {
    const nextDraft = readStoredArticleDraft();
    setForm(article ? sanitizeArticleForm({ ...article }) : nextDraft ? sanitizeArticleForm(nextDraft) : createEmptyArticle());
    setTagInput("");
    setProductFinderFilters({});
  }, [article]);

  useEffect(() => {
    if (article) return;

    writeDraft(ARTICLE_DRAFT_STORAGE_KEY, form, hasMeaningfulArticleDraft);
  }, [article, form]);

  const categoryOptions = useMemo(
    () => Array.from(new Set([...categories.map((item) => item.name), ...articleCategories])).sort((a, b) => a.localeCompare(b)),
    [articleCategories, categories]
  );

  const productOptions = useMemo(
    () =>
      Array.from(new Map([...products, ...deals].map((item) => [item.slug, item])).values())
        .map((item) => ({
          slug: item.slug,
          label: `${item.name} • ${item.marketplace}`,
          category: item.category,
          subcategory: "subcategory" in item ? item.subcategory : "",
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [deals, products]
  );

  const productCategoryFilters = useMemo(
    () => Array.from(new Set(productOptions.map((item) => item.category).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [productOptions]
  );

  const relatedArticleOptions = useMemo(
    () => articles.filter((item) => item.id !== article?.id),
    [article?.id, articles]
  );

  const setField = <K extends keyof Omit<ArticleRecord, "id">>(field: K, value: Omit<ArticleRecord, "id">[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "title") {
        const title = String(value);
        next.slug = slugify(title);
        if (!prev.metaTitle || prev.metaTitle === prev.title) {
          next.metaTitle = title;
        }
      }

      if (field === "category") {
        next.categorySlug = slugify(String(value));
      }

      if (field === "excerpt" && !prev.metaDescription) {
        next.metaDescription = String(value);
      }

      if (field === "image" && !prev.heroImage) {
        next.heroImage = String(value);
      }

      return next;
    });
  };

  const updateSection = (index: number, updater: (section: ArticleSection) => ArticleSection) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((section, currentIndex) => (currentIndex === index ? updater(section) : section)),
    }));
  };

  const addSection = (type: ArticleSection["type"]) => {
    const newSection =
      type === "products"
        ? createProductsSection()
        : type === "guide"
        ? createGuideSection()
        : createComparisonSection();

    setForm((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
  };

  const removeSection = (index: number) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const addTag = () => {
    const value = tagInput.trim();
    if (!value) return;

    setForm((prev) => ({
      ...prev,
      tags: Array.from(new Set([...prev.tags, value])),
    }));
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((item) => item !== tag),
    }));
  };

  const updateFaq = (index: number, field: keyof ArticleFaq, value: string) => {
    setForm((prev) => ({
      ...prev,
      faqs: prev.faqs.map((faq, currentIndex) => (currentIndex === index ? { ...faq, [field]: value } : faq)),
    }));
  };

  const addFaq = () => {
    setForm((prev) => ({
      ...prev,
      faqs: [...prev.faqs, createEmptyFaq()],
    }));
  };

  const removeFaq = (index: number) => {
    setForm((prev) => ({
      ...prev,
      faqs: prev.faqs.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const updateRelatedArticle = (index: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      relatedArticleSlugs: prev.relatedArticleSlugs.map((slug, currentIndex) => (currentIndex === index ? value : slug)),
    }));
  };

  const addRelatedArticle = () => {
    setForm((prev) => ({
      ...prev,
      relatedArticleSlugs: [...prev.relatedArticleSlugs, ""],
    }));
  };

  const removeRelatedArticle = (index: number) => {
    setForm((prev) => ({
      ...prev,
      relatedArticleSlugs: prev.relatedArticleSlugs.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload: Omit<ArticleRecord, "id"> = {
      ...form,
      slug: form.slug || slugify(form.title),
      categorySlug: slugify(form.category),
      metaTitle: form.metaTitle || form.title,
      metaDescription: form.metaDescription || form.excerpt,
      heroImage: form.heroImage || form.image,
      quickSummary: form.quickSummary || form.excerpt,
      tags: form.tags.filter(Boolean),
      faqs: form.faqs.filter((faq) => faq.question.trim() && faq.answer.trim()),
      relatedArticleSlugs: Array.from(new Set(form.relatedArticleSlugs.map((slug) => slug.trim()).filter(Boolean))),
      sections: form.sections.map((section) => ({
        ...section,
        id: section.id.trim() || slugify(section.title || section.type),
        title: section.title?.trim() || undefined,
        content: section.content,
        products:
          section.type === "products"
            ? (section.products ?? []).filter((item) => item.productSlug.trim())
            : undefined,
        comparisonColumns: section.type === "comparison" ? section.comparisonColumns : undefined,
        comparisonTable:
          section.type === "comparison"
            ? (section.comparisonTable ?? []).filter(
                (row) => row.feature.trim() || row.first.trim() || row.second.trim() || row.third.trim()
              )
            : undefined,
      })),
    };

    if (!article) {
      clearStoredArticleDraft();
    }

    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm p-3 md:p-5">
      <div className="bg-gray-50 rounded-3xl w-full h-full overflow-hidden border border-white/20 shadow-2xl">
        <div className="sticky top-0 z-20 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-black text-gray-900">{article ? "Editar Artigo" : "Novo Artigo"}</h2>
            <p className="text-sm text-gray-500">Modal completo em tela cheia para estruturar hero, conteúdo, produtos, FAQ e artigos relacionados.</p>
            {!article && draftWasRestored && (
              <p className="text-xs text-orange-600 mt-1">Rascunho restaurado automaticamente para você continuar de onde parou.</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!article && hasStoredArticleDraft() && (
              <button
                type="button"
                onClick={() => setForm(createEmptyArticle())}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Limpar rascunho
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="article-form"
              className="px-5 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {article ? "Salvar Alterações" : "Criar Artigo"}
            </button>
          </div>
        </div>

        <form id="article-form" onSubmit={handleSubmit} className="h-[calc(100%-81px)] overflow-y-auto px-6 py-6">
          <div className="space-y-6 max-w-7xl mx-auto pb-8">
            {!article && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                O artigo salva seu progresso automaticamente enquanto você preenche. Você pode sair, pesquisar referências e voltar depois sem perder o rascunho.
              </div>
            )}

            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="mb-5">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">Informações básicas</h3>
                <p className="text-sm text-gray-500 mt-1">Defina o conteúdo principal que aparece nas listagens, URL e hero do artigo.</p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="xl:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Título do Artigo *</label>
                  <input
                    required
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Ex: Samsung Galaxy S24 Review: Vale a Pena em 2024?"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Slug</label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50">
                    <span className="text-xs text-gray-400">/artigo/</span>
                    <input
                      value={form.slug}
                      onChange={(e) => setField("slug", e.target.value)}
                      className="flex-1 text-sm bg-transparent focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Categoria *</label>
                    <select
                      required
                      value={form.category}
                      onChange={(e) => setField("category", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
                    >
                      {categoryOptions.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Tipo *</label>
                    <select
                      required
                      value={form.type}
                      onChange={(e) => setField("type", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
                    >
                      {ARTICLE_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Tempo de leitura</label>
                    <input
                      value={form.readTime}
                      onChange={(e) => setField("readTime", e.target.value)}
                      placeholder="Ex: 12 min"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                    />
                  </div>
                </div>

                <div className="xl:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Resumo / Excerpt *</label>
                  <textarea
                    required
                    rows={3}
                    value={form.excerpt}
                    onChange={(e) => setField("excerpt", e.target.value)}
                    placeholder="Resumo curto que aparece nas listagens e ajuda no contexto do artigo."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Imagem da listagem</label>
                  <input
                    value={form.image}
                    onChange={(e) => setField("image", e.target.value)}
                    placeholder="https://..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Imagem da Hero</label>
                  <input
                    value={form.heroImage}
                    onChange={(e) => setField("heroImage", e.target.value)}
                    placeholder="https://..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                  <p className="mt-2 text-xs text-gray-500">Esta imagem é exibida no topo da página pública do artigo.</p>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="mb-5">
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">SEO, autoria e publicação</h3>
                <p className="text-sm text-gray-500 mt-1">Organize os dados de publicação, resumo rápido, autor e descrições extras.</p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Meta title</label>
                  <input
                    value={form.metaTitle}
                    onChange={(e) => setField("metaTitle", e.target.value)}
                    placeholder="Título usado em SEO"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Meta description</label>
                  <input
                    value={form.metaDescription}
                    onChange={(e) => setField("metaDescription", e.target.value)}
                    placeholder="Descrição usada em SEO"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 xl:col-span-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Data de publicação</label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setField("date", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Última atualização</label>
                    <input
                      type="date"
                      value={form.updatedDate}
                      onChange={(e) => setField("updatedDate", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Visualizações</label>
                    <input
                      type="number"
                      min={0}
                      value={form.views}
                      onChange={(e) => setField("views", Number(e.target.value) || 0)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Autor</label>
                  <input
                    value={form.author}
                    onChange={(e) => setField("author", e.target.value)}
                    placeholder="Equipe Mercadoai"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Avatar do autor</label>
                  <input
                    value={form.authorAvatar}
                    onChange={(e) => setField("authorAvatar", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>

                <div className="xl:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Resumo rápido</label>
                  <textarea
                    rows={5}
                    value={form.quickSummary}
                    onChange={(e) => setField("quickSummary", e.target.value)}
                    placeholder="Use linhas em branco para parágrafos, **texto** para negrito e - item para listas."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  />
                  <p className="mt-2 text-xs text-gray-500">Aceita parágrafos, texto em negrito com **duplo asterisco**, listas com - item e novas linhas.</p>
                </div>

                <div className="xl:col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Tags</label>

                  <div className="flex gap-2">
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      placeholder="Ex: Galaxy AI"
                      className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-black transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Adicionar tag
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-2 rounded-full bg-orange-50 text-orange-700 px-3 py-1 text-sm">
                        #{tag}
                        <button type="button" onClick={() => removeTag(tag)} className="text-orange-400 hover:text-red-500 cursor-pointer">
                          <i className="ri-close-line"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="xl:col-span-2 flex flex-wrap gap-6 pt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setField("featured", e.target.checked)} className="accent-orange-500 w-4 h-4" />
                    <span className="text-sm font-medium text-gray-700">Destacar nas listagens</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.trending} onChange={(e) => setField("trending", e.target.checked)} className="accent-orange-500 w-4 h-4" />
                    <span className="text-sm font-medium text-gray-700">Marcar como trending</span>
                  </label>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">Estrutura do conteúdo</h3>
                  <p className="text-sm text-gray-500 mt-1">Monte o artigo por blocos. O bloco de produtos usa os itens cadastrados no sistema e puxa o link de afiliado real.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => addSection("products")} className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">+ Produtos</button>
                  <button type="button" onClick={() => addSection("guide")} className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">+ Guia</button>
                  <button type="button" onClick={() => addSection("comparison")} className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">+ Comparação</button>
                </div>

              </div>

              <div className="space-y-4">
                {form.sections.map((section, sectionIndex) => (
                  <div key={`${section.id}-${sectionIndex}`} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <span className="inline-flex px-2.5 py-1 rounded-full bg-white border border-gray-200 text-xs font-bold text-gray-700 uppercase">
                          {section.type}
                        </span>
                        <p className="text-xs text-gray-500 mt-2">Bloco #{sectionIndex + 1}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSection(sectionIndex)}
                        className="w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">ID / âncora</label>
                        <input
                          value={section.id}
                          onChange={(e) => updateSection(sectionIndex, (current) => ({ ...current, id: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400"
                        />
                      </div>

                      {section.type !== "intro" && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Título da seção</label>
                          <input
                            value={section.title ?? ""}
                            onChange={(e) =>
                              updateSection(sectionIndex, (current) => ({
                                ...current,
                                title: e.target.value,
                                id: current.id || slugify(e.target.value),
                              }))
                            }
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400"
                          />
                        </div>
                      )}

                      <div className="xl:col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Conteúdo do bloco</label>
                        <textarea
                          rows={section.type === "intro" || section.type === "guide" ? 7 : 4}
                          value={section.content}
                          onChange={(e) => updateSection(sectionIndex, (current) => ({ ...current, content: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400"
                        />
                      </div>
                    </div>

                    {section.type === "products" && (
                      <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm">Produtos do bloco</h4>
                            <p className="text-xs text-gray-500">Na página pública, este bloco será exibido em grade com até 3 cards por linha.</p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              updateSection(sectionIndex, (current) => ({
                                ...current,
                                products: [...(current.products ?? []), createEmptyProductReference()],
                              }))
                            }
                            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                          >
                            + Produto
                          </button>
                        </div>

                        {productOptions.length === 0 && (
                          <p className="text-sm text-gray-400">Cadastre produtos ou ofertas no sistema para vinculá-los aqui.</p>
                        )}

                        <div className="space-y-3">
                          {(section.products ?? []).map((productRef, productIndex) => {
                            const filterKey = `${sectionIndex}-${productIndex}`;
                            const selectedProduct = productOptions.find((item) => item.slug === productRef.productSlug);
                            const selectedCategory = productFinderFilters[filterKey]?.category || selectedProduct?.category || "";
                            const availableSubcategories = Array.from(
                              new Set(
                                productOptions
                                  .filter((item) => !selectedCategory || item.category === selectedCategory)
                                  .map((item) => item.subcategory)
                                  .filter(Boolean)
                              )
                            ).sort((a, b) => a.localeCompare(b));
                            const selectedSubcategory =
                              productFinderFilters[filterKey]?.subcategory && availableSubcategories.includes(productFinderFilters[filterKey]!.subcategory)
                                ? productFinderFilters[filterKey]!.subcategory
                                : selectedProduct?.subcategory && availableSubcategories.includes(selectedProduct.subcategory)
                                ? selectedProduct.subcategory
                                : "";
                            const filteredOptions = productOptions.filter(
                              (item) =>
                                (!selectedCategory || item.category === selectedCategory) &&
                                (!selectedSubcategory || item.subcategory === selectedSubcategory)
                            );
                            const visibleProductOptions =
                              selectedProduct && !filteredOptions.some((item) => item.slug === selectedProduct.slug)
                                ? [selectedProduct, ...filteredOptions]
                                : filteredOptions;

                            return (
                              <div key={productIndex} className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr_auto] gap-3 items-start rounded-2xl border border-gray-200 bg-gray-50 p-3">
                                <div className="space-y-3">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Filtrar por categoria</label>
                                      <select
                                        value={selectedCategory}
                                        onChange={(e) =>
                                          setProductFinderFilters((prev) => ({
                                            ...prev,
                                            [filterKey]: { category: e.target.value, subcategory: "" },
                                          }))
                                        }
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
                                      >
                                        <option value="">Todas as categorias</option>
                                        {productCategoryFilters.map((category) => (
                                          <option key={category} value={category}>{category}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Filtrar por subcategoria</label>
                                      <select
                                        value={selectedSubcategory}
                                        onChange={(e) =>
                                          setProductFinderFilters((prev) => ({
                                            ...prev,
                                            [filterKey]: { category: selectedCategory, subcategory: e.target.value },
                                          }))
                                        }
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 cursor-pointer disabled:bg-gray-100 disabled:text-gray-400"
                                        disabled={availableSubcategories.length === 0}
                                      >
                                        <option value="">Todas as subcategorias</option>
                                        {availableSubcategories.map((subcategory) => (
                                          <option key={subcategory} value={subcategory}>{subcategory}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Produto vinculado</label>
                                    <select
                                      value={productRef.productSlug}
                                      onChange={(e) =>
                                        updateSection(sectionIndex, (current) => ({
                                          ...current,
                                          products: (current.products ?? []).map((item, currentIndex) =>
                                            currentIndex === productIndex ? { ...item, productSlug: e.target.value } : item
                                          ),
                                        }))
                                      }
                                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
                                    >
                                      <option value="">Selecione um produto</option>
                                      {visibleProductOptions.map((option) => (
                                        <option key={option.slug} value={option.slug}>{option.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Badge</label>
                                    <input
                                      value={productRef.badge}
                                      onChange={(e) =>
                                        updateSection(sectionIndex, (current) => ({
                                          ...current,
                                          products: (current.products ?? []).map((item, currentIndex) =>
                                            currentIndex === productIndex ? { ...item, badge: e.target.value } : item
                                          ),
                                        }))
                                      }
                                      placeholder="Ex: Melhor custo-benefício"
                                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Resumo do destaque</label>
                                  <textarea
                                    rows={4}
                                    value={productRef.highlight}
                                    onChange={(e) =>
                                      updateSection(sectionIndex, (current) => ({
                                        ...current,
                                        products: (current.products ?? []).map((item, currentIndex) =>
                                          currentIndex === productIndex ? { ...item, highlight: e.target.value } : item
                                        ),
                                      }))
                                    }
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 resize-none"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateSection(sectionIndex, (current) => ({
                                      ...current,
                                      products: (current.products ?? []).filter((_, currentIndex) => currentIndex !== productIndex),
                                    }))
                                  }
                                  className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {section.type === "comparison" && (
                      <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Coluna 1</label>
                            <input
                              value={section.comparisonColumns?.first ?? ""}
                              onChange={(e) =>
                                updateSection(sectionIndex, (current) => ({
                                  ...current,
                                  comparisonColumns: { ...(current.comparisonColumns ?? { first: "", second: "", third: "" }), first: e.target.value },
                                }))
                              }
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-orange-400"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Coluna 2</label>
                            <input
                              value={section.comparisonColumns?.second ?? ""}
                              onChange={(e) =>
                                updateSection(sectionIndex, (current) => ({
                                  ...current,
                                  comparisonColumns: { ...(current.comparisonColumns ?? { first: "", second: "", third: "" }), second: e.target.value },
                                }))
                              }
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-orange-400"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Coluna 3</label>
                            <input
                              value={section.comparisonColumns?.third ?? ""}
                              onChange={(e) =>
                                updateSection(sectionIndex, (current) => ({
                                  ...current,
                                  comparisonColumns: { ...(current.comparisonColumns ?? { first: "", second: "", third: "" }), third: e.target.value },
                                }))
                              }
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:border-orange-400"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-3">
                          <h4 className="font-bold text-gray-900 text-sm">Linhas da tabela</h4>
                          <button
                            type="button"
                            onClick={() =>
                              updateSection(sectionIndex, (current) => ({
                                ...current,
                                comparisonTable: [
                                  ...(current.comparisonTable ?? []),
                                  { feature: "", first: "", second: "", third: "" },
                                ],
                              }))
                            }
                            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                          >
                            + Linha
                          </button>
                        </div>

                        <div className="space-y-3">
                          {(section.comparisonTable ?? []).map((row, rowIndex) => (
                            <div key={rowIndex} className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr_1fr_1fr_auto] gap-3 items-start rounded-2xl border border-gray-200 bg-gray-50 p-3">
                              {([
                                { key: "feature", label: "Característica" },
                                { key: "first", label: "Valor 1" },
                                { key: "second", label: "Valor 2" },
                                { key: "third", label: "Valor 3" },
                              ] as const).map((field) => (
                                <div key={field.key}>
                                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{field.label}</label>
                                  <input
                                    value={row[field.key]}
                                    onChange={(e) =>
                                      updateSection(sectionIndex, (current) => ({
                                        ...current,
                                        comparisonTable: (current.comparisonTable ?? []).map((item, currentIndex) =>
                                          currentIndex === rowIndex ? { ...item, [field.key]: e.target.value } : item
                                        ),
                                      }))
                                    }
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400"
                                  />
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() =>
                                  updateSection(sectionIndex, (current) => ({
                                    ...current,
                                    comparisonTable: (current.comparisonTable ?? []).filter((_, currentIndex) => currentIndex !== rowIndex),
                                  }))
                                }
                                className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div>
                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">Perguntas frequentes</h3>
                    <p className="text-sm text-gray-500 mt-1">Cada pergunta entra no bloco FAQ e no schema do artigo.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addFaq}
                    className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    + FAQ
                  </button>
                </div>

                <div className="space-y-3">
                  {form.faqs.map((faq, faqIndex) => (
                    <div key={faqIndex} className="rounded-2xl border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-bold text-gray-500 uppercase">Pergunta #{faqIndex + 1}</span>
                        <button type="button" onClick={() => removeFaq(faqIndex)} className="text-gray-400 hover:text-red-500 cursor-pointer">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                      <input
                        value={faq.question}
                        onChange={(e) => updateFaq(faqIndex, "question", e.target.value)}
                        placeholder="Pergunta"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400"
                      />
                      <textarea
                        rows={4}
                        value={faq.answer}
                        onChange={(e) => updateFaq(faqIndex, "answer", e.target.value)}
                        placeholder="Resposta"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 resize-none"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div>
                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">Artigos relacionados</h3>
                    <p className="text-sm text-gray-500 mt-1">Selecione quais artigos aparecem na barra lateral do conteúdo.</p>
                  </div>
                  <button
                    type="button"
                    onClick={addRelatedArticle}
                    className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
                  >
                    + Relacionado
                  </button>
                </div>

                <div className="space-y-3">
                  {form.relatedArticleSlugs.map((relatedSlug, relatedIndex) => (
                    <div key={`${relatedSlug}-${relatedIndex}`} className="flex gap-3 items-start rounded-2xl border border-gray-200 bg-gray-50 p-3">
                      <select
                        value={relatedSlug}
                        onChange={(e) => updateRelatedArticle(relatedIndex, e.target.value)}
                        className="flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
                      >
                        <option value="">Selecione um artigo</option>
                        {relatedArticleOptions.map((relatedArticle) => (
                          <option key={relatedArticle.id} value={relatedArticle.slug}>{relatedArticle.title}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeRelatedArticle(relatedIndex)}
                        className="w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-400 hover:text-red-500 hover:border-red-200 cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}

                  {form.relatedArticleSlugs.length === 0 && (
                    <p className="text-sm text-gray-400">Nenhum artigo relacionado selecionado.</p>
                  )}
                </div>

                <div className="mt-6 space-y-4 border-t border-gray-100 pt-5">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">Bio do autor</label>
                    <textarea
                      rows={3}
                      value={form.authorBio}
                      onChange={(e) => setField("authorBio", e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-orange-400 focus:outline-none resize-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">Disclosure / aviso de afiliado</label>
                    <textarea
                      rows={4}
                      value={form.disclosure}
                      onChange={(e) => setField("disclosure", e.target.value)}
                      placeholder="Este artigo contém links de ofertas. Se você comprar através desses links, podemos receber uma comissão sem custo adicional para você. Isso nos ajuda a manter o site e continuar produzindo conteúdo gratuito e independente."
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:border-orange-400 focus:outline-none resize-none"
                    />
                  </div>
                </div>
              </div>
            </section>

          </div>
        </form>
      </div>
    </div>
  );
}